import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Server,
    Send,
    Loader2,
    ShieldCheck,
    Save,
    PlugZap,
    Eye,
    EyeOff,
} from "lucide-react";
import api, { formatApiError } from "./api";

const PRESETS = [
    {
        name: "Custom (manual)",
        host: "",
        imap_host: "",
        imap_port: 993,
        imap_ssl: true,
        smtp_host: "",
        smtp_port: 587,
        smtp_ssl: false,
    },
    {
        name: "Outlook / Hotmail",
        imap_host: "outlook.office365.com",
        imap_port: 993,
        imap_ssl: true,
        smtp_host: "smtp.office365.com",
        smtp_port: 587,
        smtp_ssl: false,
    },
    {
        name: "Yahoo Mail",
        imap_host: "imap.mail.yahoo.com",
        imap_port: 993,
        imap_ssl: true,
        smtp_host: "smtp.mail.yahoo.com",
        smtp_port: 465,
        smtp_ssl: true,
    },
    {
        name: "Zoho Mail",
        imap_host: "imap.zoho.com",
        imap_port: 993,
        imap_ssl: true,
        smtp_host: "smtp.zoho.com",
        smtp_port: 465,
        smtp_ssl: true,
    },
    {
        name: "iCloud",
        imap_host: "imap.mail.me.com",
        imap_port: 993,
        imap_ssl: true,
        smtp_host: "smtp.mail.me.com",
        smtp_port: 587,
        smtp_ssl: false,
    },
];

const EMPTY = {
    imap_host: "",
    imap_port: 993,
    imap_user: "",
    imap_password: "",
    imap_ssl: true,
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_ssl: false,
};

export default function PortalMailboxSetup() {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [savedHints, setSavedHints] = useState({
        imap_password_set: false,
        smtp_password_set: false,
    });
    const [showPwd, setShowPwd] = useState({ imap: false, smtp: false });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/portal/mailbox");
                if (data.mailbox) {
                    setForm((f) => ({
                        ...f,
                        imap_host: data.mailbox.imap_host || "",
                        imap_port: data.mailbox.imap_port || 993,
                        imap_user: data.mailbox.imap_user || "",
                        imap_ssl: !!data.mailbox.imap_ssl,
                        smtp_host: data.mailbox.smtp_host || "",
                        smtp_port: data.mailbox.smtp_port || 587,
                        smtp_user: data.mailbox.smtp_user || "",
                        smtp_ssl: !!data.mailbox.smtp_ssl,
                    }));
                    setSavedHints({
                        imap_password_set: !!data.mailbox.imap_password_set,
                        smtp_password_set: !!data.mailbox.smtp_password_set,
                    });
                }
            } catch (e) {
                // ignore
            }
        })();
    }, []);

    const set = (k) => (e) => {
        const v =
            e.target.type === "checkbox"
                ? e.target.checked
                : e.target.type === "number"
                  ? Number(e.target.value)
                  : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const applyPreset = (idx) => {
        const p = PRESETS[idx];
        if (!p) return;
        setForm((f) => ({
            ...f,
            imap_host: p.imap_host,
            imap_port: p.imap_port,
            imap_ssl: p.imap_ssl,
            smtp_host: p.smtp_host,
            smtp_port: p.smtp_port,
            smtp_ssl: p.smtp_ssl,
        }));
    };

    const save = async (e) => {
        e?.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            const payload = { ...form };
            // Don't send empty password if already saved
            if (!payload.imap_password && savedHints.imap_password_set)
                delete payload.imap_password;
            if (!payload.smtp_password && savedHints.smtp_password_set)
                delete payload.smtp_password;
            await api.put("/portal/mailbox", payload);
            toast.success("Mailbox saved", {
                description: "Credentials encrypted and stored.",
            });
            setSavedHints({
                imap_password_set: true,
                smtp_password_set: true,
            });
            setForm((f) => ({ ...f, imap_password: "", smtp_password: "" }));
        } catch (e) {
            toast.error("Could not save", {
                description: formatApiError(e?.response?.data?.detail) || e.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const test = async () => {
        if (testing) return;
        setTesting(true);
        try {
            const { data } = await api.post("/portal/mailbox/test");
            const both = data.imap_ok && data.smtp_ok;
            if (both) {
                toast.success("Connection successful", {
                    description: "IMAP and SMTP both authenticated.",
                });
            } else {
                toast.warning("Partial result", {
                    description: `IMAP ${data.imap_ok ? "OK" : "FAIL: " + (data.imap_error || "")}; SMTP ${data.smtp_ok ? "OK" : "FAIL: " + (data.smtp_error || "")}`,
                });
            }
        } catch (e) {
            toast.error("Test failed", {
                description: formatApiError(e?.response?.data?.detail) || e.message,
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="mailbox-setup-page"
        >
            <div className="pr-glass-strong rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-400/30 flex items-center justify-center">
                        <Server className="w-5 h-5 text-blue-300" />
                    </div>
                    <div>
                        <h1 className="font-outfit text-2xl font-bold text-white">
                            Mailbox setup
                        </h1>
                        <p className="text-sm text-slate-400">
                            IMAP for reading, SMTP for sending. Passwords are
                            encrypted at rest with AES.
                        </p>
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                    {PRESETS.map((p, i) => (
                        <button
                            key={p.name}
                            type="button"
                            onClick={() => applyPreset(i)}
                            className="text-xs font-outfit px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-slate-300 hover:border-blue-400/40 hover:text-blue-200 transition-colors"
                            data-testid={`mailbox-preset-${i}`}
                        >
                            {p.name}
                        </button>
                    ))}
                </div>

                <form onSubmit={save} className="mt-7 grid lg:grid-cols-2 gap-7" data-testid="mailbox-form">
                    {/* IMAP */}
                    <div className="space-y-4">
                        <SectionTitle text="IMAP — incoming" />
                        <Field
                            label="IMAP host"
                            value={form.imap_host}
                            onChange={set("imap_host")}
                            placeholder="imap.example.com"
                            testId="imap-host"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Field
                                label="Port"
                                type="number"
                                value={form.imap_port}
                                onChange={set("imap_port")}
                                testId="imap-port"
                            />
                            <Toggle
                                label="Use SSL"
                                checked={form.imap_ssl}
                                onChange={set("imap_ssl")}
                                testId="imap-ssl"
                            />
                        </div>
                        <Field
                            label="IMAP username"
                            value={form.imap_user}
                            onChange={set("imap_user")}
                            placeholder="you@example.com"
                            testId="imap-user"
                        />
                        <PasswordField
                            label="IMAP password"
                            value={form.imap_password}
                            onChange={set("imap_password")}
                            saved={savedHints.imap_password_set}
                            shown={showPwd.imap}
                            onToggleShow={() =>
                                setShowPwd((s) => ({ ...s, imap: !s.imap }))
                            }
                            testId="imap-password"
                        />
                    </div>

                    {/* SMTP */}
                    <div className="space-y-4">
                        <SectionTitle text="SMTP — outgoing" />
                        <Field
                            label="SMTP host"
                            value={form.smtp_host}
                            onChange={set("smtp_host")}
                            placeholder="smtp.example.com"
                            testId="smtp-host"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Field
                                label="Port"
                                type="number"
                                value={form.smtp_port}
                                onChange={set("smtp_port")}
                                testId="smtp-port"
                            />
                            <Toggle
                                label="Use SSL (port 465)"
                                checked={form.smtp_ssl}
                                onChange={set("smtp_ssl")}
                                testId="smtp-ssl"
                            />
                        </div>
                        <Field
                            label="SMTP username"
                            value={form.smtp_user}
                            onChange={set("smtp_user")}
                            placeholder="you@example.com"
                            testId="smtp-user"
                        />
                        <PasswordField
                            label="SMTP password"
                            value={form.smtp_password}
                            onChange={set("smtp_password")}
                            saved={savedHints.smtp_password_set}
                            shown={showPwd.smtp}
                            onToggleShow={() =>
                                setShowPwd((s) => ({ ...s, smtp: !s.smtp }))
                            }
                            testId="smtp-password"
                        />
                    </div>

                    <div className="lg:col-span-2 flex flex-wrap gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary disabled:opacity-50"
                            data-testid="mailbox-save"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Save mailbox
                        </button>
                        <button
                            type="button"
                            disabled={testing || (!savedHints.imap_password_set && !form.imap_password)}
                            onClick={test}
                            className="btn-secondary disabled:opacity-50"
                            data-testid="mailbox-test"
                        >
                            {testing ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <PlugZap className="w-4 h-4" />
                            )}
                            Test connection
                        </button>
                        <span className="ml-auto inline-flex items-center gap-2 text-[11px] font-mono-pr text-slate-500">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                            AES-encrypted at rest · never sent to frontend
                        </span>
                    </div>
                </form>
            </div>
        </motion.div>
    );
}

function SectionTitle({ text }) {
    return (
        <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold flex items-center gap-2">
            <Send className="w-3.5 h-3.5" />
            {text}
        </p>
    );
}

function Field({ label, type = "text", value, onChange, placeholder, testId }) {
    return (
        <div>
            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="input-glow w-full rounded-xl py-2.5 px-4 text-sm text-white placeholder:text-slate-500"
                data-testid={testId}
            />
        </div>
    );
}

function Toggle({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center justify-between gap-3 input-glow rounded-xl py-2.5 px-4 cursor-pointer">
            <span className="text-xs text-slate-300 font-outfit">{label}</span>
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                className="accent-blue-500 w-4 h-4"
                data-testid={testId}
            />
        </label>
    );
}

function PasswordField({ label, value, onChange, saved, shown, onToggleShow, testId }) {
    return (
        <div>
            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                {label}
                {saved && (
                    <span className="ml-2 text-[10px] text-emerald-300 font-mono-pr normal-case tracking-normal">
                        · saved (leave blank to keep)
                    </span>
                )}
            </label>
            <div className="relative">
                <input
                    type={shown ? "text" : "password"}
                    value={value}
                    onChange={onChange}
                    placeholder={saved ? "•••••••• (saved)" : "Enter password"}
                    autoComplete="new-password"
                    className="input-glow w-full rounded-xl py-2.5 pl-4 pr-10 text-sm text-white placeholder:text-slate-500"
                    data-testid={testId}
                />
                <button
                    type="button"
                    onClick={onToggleShow}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md text-slate-400 hover:text-white"
                    aria-label="Toggle visibility"
                >
                    {shown ? (
                        <EyeOff className="w-4 h-4" />
                    ) : (
                        <Eye className="w-4 h-4" />
                    )}
                </button>
            </div>
        </div>
    );
}
